import {
  Box,
  Button,
  Checkbox,
  Fab,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@mui/material";
import {NpiTemplateFilter, NpiTemplateService} from "../../../../../../services/npi-template-service";
import {useFilters} from "../../../../../../filters/base-filter";
import * as React from "react";
import {PaginatedResult} from "../../../../../../models/default/paginated-result";
import {
  calendarTypeLabels,
  Calendar,
  Template
} from "../../../../../../models/basic/template";
import {usePagination} from "../../../../../../utils/pagination";
import * as Yup from "yup";
import {ErrorComponent} from "../../../../../../components/error/error.component";
import {useEffect} from "react";
import {Project} from "../../../../../../models/basic/project";
import {toast} from "react-toastify";
import {translate} from "../../../../../../translate/translate";
import {Add, Search} from "@mui/icons-material";
import {Field, Form, Formik, FormikHelpers} from "formik";
import TablePaginationComponent from "../../../../../../components/table/table-pagination.component";
import TableActionComponent from "../../../../../../components/table/table-action.component";
import GenericDialog from "../../../../../../components/dialog/generic-dialog.component";
import {NpiCalendarService} from "../../../../../../services/npi-calendar-service";
import {useNavigate} from "react-router-dom";
import {URLS} from "../../../../../../services/routes";
import CrudSelectProjectTypeComponent from "./crud-select-project-type.component";

const NpiTemplateComponent = () => {
  const service = new NpiTemplateService();
  const calendarService = new NpiCalendarService();
  const navigate = useNavigate();
  const {filters, updateFilter, updateFilterKeepingParam, resetFilters } = useFilters<NpiTemplateFilter>(
    { search: "" }, ["search"]
  );

  const [templateList, setTemplateList] = React.useState<PaginatedResult<Template>>();
  const [copyTemplateList, setCopyTemplateList] = React.useState<Template[]>([]);
  const [calendarList, setCalendarList] = React.useState<Calendar[]>([]);

  const {
    dataSource,
    pagination,
    resetPage,
    handleChangePage,
    handleChangeRowsPerPage
  } = usePagination<Template>(templateList);

  const [object, setObject] = React.useState(new Template());
  const [openDialog, setOpenDialog] = React.useState(false);
  const [invalidForm, setInvalidForm] = React.useState(true);

  const schema = Yup.object().shape({
    name: Yup.string()
      .required(),
    project_type: Yup.string().required(),
    calendars: Yup.array().min(1).required(),
  });

  const handleOpenEdit = (row: Template) => {
    navigate(URLS.NPI_TEMPLATE_DETAIL.replace(":id", String(row.id)));
  }

  const handleOpenNew = () => {
    setObject(new Template());
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setInvalidForm(true);
    setOpenDialog(false);
    setCopyTemplateList([]);
    setObject(new Template());
  };

  const search = (restartIndex = false) => {
    service.readPaginated(filters, [], pagination).then(response => {
      setTemplateList(response.data);
    }).catch((error: any) => {
      ErrorComponent(error);
    });

    restartIndex && resetPage();
  };

  useEffect(() => {
    search();
  }, [pagination]);

  useEffect(() => {
    if (!openDialog) {
      return;
    }

    calendarService.readAll().then(response => {
      setCalendarList(response.data);
      setObject((currentObject) => ({
        ...currentObject,
        calendars: response.data.map((calendar) => calendar.id!!),
      }));
    }).catch((error: any) => ErrorComponent(error));

    service.readAll().then(response => {
      setCopyTemplateList(response.data.filter(item => item.active));
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  }, [openDialog]);

  const handleDelete = (row: Project) => {
    service.delete(Number(row.id)).then(() => {
      toast.success(translate.t("successfully_deleted"));
      resetFilters();
      resetPage();
      search();
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  const handleSwitch = (row: Project, column: keyof Project) => {
    (row[column] as boolean) = !row[column];
    const payload = {"active": row.active};
    service.patch(Number(row.id), payload).then(() => {
      toast.success(translate.t("successfully_updated"));
      search();
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  const handleSubmit = (values: Template) => {
    setTimeout(() => {
      const payload = {
        name: values.name.trim(),
        project_type: values.project_type,
        calendars: values.calendars,
        amount_of_days: values.amount_of_days,
        active: values.active,
      };
      if (values.id !== undefined) {
        service.patch(values.id, payload).then(() => {
          toast.success(translate.t("successfully_updated"));
          handleCloseDialog();
          search(true);
        }).catch((error: any) => {
          ErrorComponent(error);
        });
      } else {
        service.create({
          ...payload,
          copy_template: values.copy_template || undefined,
        } as Template)
          .then(() => {
            toast.success(translate.t("successfully_registered"));
            handleCloseDialog();
            search(true);
          })
          .catch((error: any) => {
            ErrorComponent(error);
          });
      }
    }, 500);
  }

  const handleObjChange = (values: Template) => {
    setObject(values);
    setInvalidForm(
      !values.name.trim()
      || !values.project_type
      || !values.calendars.length
    );
  }

  const handleCalendarChange = (calendar: number) => {
    const selectedCalendars = object.calendars.includes(calendar)
      ? object.calendars.filter((item) => item !== calendar)
      : [...object.calendars, calendar];

    handleObjChange({
      ...object,
      calendars: selectedCalendars,
    });
  };

  return (
    <section>
      <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
        <Typography variant={"h5"}>
          {translate.t("planning_template")}
        </Typography>
      </div>
      <div className="floating-button">
        <Button
          color="secondary"
          variant="contained"
          startIcon={<Add/>}
          onClick={() => {
            handleOpenNew();
          }}>{translate.t("new_template").toUpperCase()}</Button>
      </div>

      <Grid size={12}>
        <Paper variant="outlined">
          <Box p={2}>
            <Formik
              enableReinitialize={true}
              validateOnMount={true}
              initialValues={{name: ""}}
              onSubmit={(values, {setSubmitting}) => {
                setTimeout(() => {
                  search(true);
                  setSubmitting(false);
                }, 500);
              }}
            >
              {
                () => (
                  <Form autoComplete="off">
                    <Grid alignItems="center" container spacing={2} direction={"row"} width="50%">
                      <Grid size={5}>
                        <Field
                          as={TextField}
                          name="search"
                          label={""}
                          fullWidth
                          variant={"outlined"}
                          autoFocus={true}
                          placeholder={translate.t("search_template")}
                          value={filters.search}
                          onChange={(event: React.ChangeEvent<{
                            value: unknown
                          }>) => updateFilterKeepingParam("search", event.target.value)}
                        />
                      </Grid>
                      <Grid >
                        <Fab
                          size="small"
                          type="submit"
                          color="secondary">
                          <Search/>
                        </Fab>
                      </Grid>
                    </Grid>
                  </Form>
                )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <Paper variant="outlined" sx={{marginTop: "16px"}}>
        <TablePaginationComponent
          state={dataSource}
          displayedColumnsShow={["Templates", "Status", "project_type", "action"]}
          displayedColumns={["name", "active", "project_type_name", "action"]}
          handleDelete={handleDelete}
          handleSwitch={handleSwitch}
          editWithDialog={handleOpenEdit}
          handleDoubleClick={handleOpenEdit}
          count={templateList?.count}
          next={templateList?.next}
          previous={templateList?.previous}
          page={pagination.page}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          actionsComponent={TableActionComponent}
          columnStyles={{1:"75%", 3:"15%", 4:"10%"}}
          showActions={["edit", "delete"]}
          isNpiTemplate
        />
      </Paper>
      <GenericDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={translate.t("new_template")}
        colorButton={"primary"}
        labelButton={"save"}
        disableConfirm={invalidForm}
        handleConfirm={() => handleSubmit(object)}>
        <Box>
          <FormControl fullWidth>
            <Formik
              id="dialog-form"
              enableReinitialize
              initialValues={object}
              validationSchema={schema}
              onSubmit={(values: Template, {resetForm}: FormikHelpers<Template>) => {
                handleSubmit(values);
                resetForm();
                handleCloseDialog();
              }}
              validateOnChange={true}>
              {({errors, touched}) => (
                <Form autoComplete="off">
                  <Grid container spacing={2} direction={"column"} width="100%">
                    <Grid >
                      <Field
                        as={TextField}
                        name="name"
                        label={translate.t("template_name")}
                        variant={"outlined"}
                        fullWidth
                        autoFocus={true}
                        required={true}
                        value={object.name}
                        error={touched.name && Boolean(errors.name) && invalidForm}
                        onChange={(event: React.ChangeEvent<{
                          value: unknown
                        }>) => {
                          const obj = object;
                          const value = event.target.value as string;
                          if (value.length > 256) return;
                          handleObjChange({
                            ...obj,
                            name: value as string,
                          });
                        }}
                        inputProps={{maxLength: 256}}/>
                    </Grid>
                    <Grid >
                      <CrudSelectProjectTypeComponent
                        value={object.project_type}
                        error={touched.project_type && Boolean(errors.project_type) && invalidForm}
                        onChange={(value) => {
                          handleObjChange({
                            ...object,
                            project_type: value,
                          });
                        }}
                        showOnlyAvailableTypes={true}
                      />
                    </Grid>
                    <Grid width="100%">
                      <FormControl fullWidth>
                        <InputLabel id="npi-copy-template-label">{translate.t("copy_template")}</InputLabel>
                        <Select
                          labelId="npi-copy-template-label"
                          label={translate.t("copy_template")}
                          value={object.copy_template}
                          sx={{
                            "& .MuiTypography-root": {
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap"
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                maxWidth: 552
                              },
                            },
                          }}
                          onChange={(event) => {
                            handleObjChange({
                              ...object,
                              copy_template: event.target.value as number | "",
                            });
                          }}>
                          <MenuItem value="">
                            <em>{translate.t("none")}</em>
                          </MenuItem>
                          {copyTemplateList.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              <Typography sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                width: "100%"
                              }}>{item.name}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid>
                      <Typography fontWeight={700} mb={1}>{translate.t("calendar")}:</Typography>
                      <FormGroup>
                        {calendarList.map((calendar) => (
                          <FormControlLabel
                            key={calendar.id}
                            control={
                              <Checkbox
                                checked={object.calendars.includes(calendar.id!!)}
                                onChange={() => handleCalendarChange(calendar.id!!)}
                              />
                            }
                            label={calendarTypeLabels[calendar.type]}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </FormControl>
        </Box>
      </GenericDialog>
    </section>
  );
};

export default NpiTemplateComponent;
